import { trackTiming } from './utils/telemetry';

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import 'phoenix_html';
import './react_component_binder';

// Establish Phoenix Socket and LiveView configuration.
import { Socket } from 'phoenix';
import { LiveSocket, ViewHook } from 'phoenix_live_view';

import { Application } from '@hotwired/stimulus';
import accordionsController from './controllers/accordions_controller';
import accordionsListController from './controllers/accordions_list_controller';
import accountController from './controllers/account_controller';
import accountOtpController from './controllers/account_otp_controller';
import accountGuestController from './controllers/account_guest_controller';
import accountFormGuestCheckoutController from './controllers/account_form_guest_checkout_controller';
import accountFormSignInController from './controllers/account_form_sign_in_controller';
import accountFormSignUpController from './controllers/account_form_sign_up_controller';
import carouselController from './controllers/carousel_controller';
import swiperCarouselController from './controllers/carousel_swiper_controller';
import cartDrawerController from './controllers/cart_drawer_controller';
import cartIconController from './controllers/cart_icon_controller';
import cartItemController from './controllers/cart_item_controller';
import collectionController from './controllers/collection_controller';
import containerController from './controllers/container_controller';
import countdownController from './controllers/countdown_controller';
import countryMenuController from './controllers/country_menu_controller';
import dropdownController from './controllers/dropdown_controller';
import engravingController from './controllers/engraving_controller';
import faqController from './controllers/faq_controller';
import focusTrapController from './controllers/focus_trap_controller';
import gnavScrollController from './controllers/gnav_scroll_controller';
import horizontalScrollController from './controllers/horizontal_scroll_controller';
import iframeController from './controllers/iframe_controller';
import imageComparisonSliderController from './controllers/image_comparison_slider_controller';
import imageErrorController from './controllers/image_error_controller';
import ImageLazyLoad from './controllers/image_lazy_load_controller';
import languageSelectorController from './controllers/language_selector_controller';
import LazyVideoController from './controllers/lazy_video_controller';
import liveChatController from './controllers/livechat_controller';
import loyaltyNavController from './controllers/loyalty_nav_controller';
import loyaltySignUpController from './controllers/loyalty_signup_controller';
import menuController from './controllers/menu_controller';
import minimalHeaderController from './controllers/minimal_header_controller';
import modalController from './controllers/modal_controller';
import navController from './controllers/nav_controller';
import followElementController from './controllers/follow_element_controller';
import numberIncrementer from './controllers/number_incrementer_controller';
import offers_controller from './controllers/offers_controller';
import pageTakeover from './controllers/page_takeover_controller';
import passwordRulesController from './controllers/password_rules_controller';
import passwordStrengthController from './controllers/password_strength_controller';
import perlgemCartController from './controllers/perlgem_cart_controller';
import PowerReviewsController from './controllers/power_reviews_controller';
import productController from './controllers/product_controller';
import promoController from './controllers/promo_controller';
import revisionTagController from './controllers/revision_tag_controller';
import salonCommissionController from './controllers/salon_commission_controller';
import searchController from './controllers/search_controller';
import secondaryGnavController from './controllers/secondary_gnav_controller';
import selectListController from './controllers/select_list_controller';
import shadesController from './controllers/shades_controller';
import shadesDropdownController from './controllers/shades_dropdown_controller';
import signupController from './controllers/signup_controller';
import debugController from './controllers/debug_controller';
import stackedNavController from './controllers/stacked_nav_controller';
import selectController from './controllers/select_controller';
import StickyFooterFeedController from './controllers/sticky_footer_feed_controller';
import StickyFooterObservableController from './controllers/sticky_footer_observable_controller';
import tabbedToutController from './controllers/tabbed-tout-controller';
import videoController from './controllers/video_controller';
import videoResponsiveController from './controllers/video_responsive_controller';
import videoSectionController from './controllers/video_playback_controller';
import vtoController from './controllers/vto_controller';
import { Accordion, AccordionEffects } from './hooks/accordion';
import { filterPopup } from './hooks/filter_popup';
import { HookRelay } from './hooks/hook_relay';
import { Arcade } from './hooks/arcade';
import { CopyLink } from './hooks/copy_link';
import { LogSvgNotFound } from './hooks/log_svg_not_found';
import { ProductDetailsAccordion } from './hooks/product_details_accordion';
import { ShowStickyCTAButton } from './hooks/show_sticky_cta_button';
import { ProductRecommendations } from './hooks/product_recommendations';
import { Experiments } from './hooks/experiment';
import { TickerTape } from './hooks/ticker_tape';
import { accessibilitySubNavToggle } from './hooks/accessibility_sub_nav_toggle';
import { accessibilityDesktopNav } from './hooks/accessibility_desktop_nav';
import { predictiveSearch } from './hooks/predictive_search';
import { PixleeHook } from './hooks/pixlee';
import LivePerson from './live_person/live_person_setup';
import MobileHeaderController from './controllers/mobile_header_controller';
import HeaderScrollController from './controllers/header_scroll_controller';
import GnavAccountController from './controllers/gnav_account_controller';
import { ttRatingsAndReviews } from './hooks/tt_ratings_and_reviews';

import './accessibility';
import './analytics';
import './cart/injection';
import './experimentation';
import './webcomponents';
import './utils/jumpToAnchor';
import { filters } from './hooks/filters';

export type VulcanThis = Pick<
  ViewHook,
  'el' | 'viewName' | 'pushEvent' | 'pushEventTo' | 'handleEvent'
>;

export type VulcanHook = Partial<
  Omit<ViewHook, 'el' | 'viewName' | 'pushEvent' | 'pushEventTo' | 'handleEvent'>
>;

interface IStimulusConfig {
  checks_enabled: boolean;
}

// extend Window interface to include vulcan specifics
declare global {
  interface Window {
    _platform: string;
    _be_platform: string;
    liveSocket: LiveSocket;
    Stimulus: Application;
    StimulusConfig?: IStimulusConfig;
    AccordionEffects?: typeof AccordionEffects;
  }
}

window._platform = 'vulcan';
// perlgem will be the default platform if `_be_platform` is not available globally.
window._be_platform ||= 'perlgem';

const Hooks = {
  Accordion,
  Arcade,
  CopyLink,
  Experiments,
  HookRelay,
  LogSvgNotFound,
  PixleeHook,
  ProductDetailsAccordion,
  ProductRecommendations,
  ShowStickyCTAButton,
  TickerTape,
  accessibilityDesktopNav,
  accessibilitySubNavToggle,
  filterPopup,
  filters,
  predictiveSearch,
  ttRatingsAndReviews
};

const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content');
const liveSocket = new LiveSocket('/live', Socket, {
  params: { _csrf_token: csrfToken },
  hooks: Hooks,
  dom: {
    // Allow `phx-update="ignore"` -like behavior but for specific attributes of
    // an element. Elements with attributes like `phx-keep-class` and
    // `phx-keep-style` will prevent LiveView from replacing those attributes
    // during diffs, which often interferes with client-side JS. Jose Valim is
    // aware of the need for this but the Phoenix team has not yet come to a
    // general solution:
    // https://github.com/phoenixframework/phoenix_live_view/issues/2802#issuecomment-1715422729
    // See the exact adaptation of this pattern from WyeWorks here:
    // https://www.wyeworks.com/blog/2024/02/27/integrating-live-view-and-js/
    onBeforeElUpdated(from, to) {
      for (const attr of from.attributes) {
        if (attr.name.startsWith('phx-keep-')) {
          const attrName = attr.name.substring(9);
          if (from.hasAttribute(attrName)) {
            to.setAttribute(attrName, from.getAttribute(attrName));
          } else {
            to.removeAttribute(attrName);
          }
        }
      }
    }
  }
});

// assign the default `AccordionEffects` if no explicit override exists
if (!('AccordionEffects' in window)) {
  window.AccordionEffects = AccordionEffects;
}

trackTiming('stimulus_starting');
const Stimulus = (window.Stimulus = Application.start());

Stimulus.register('product', productController);
Stimulus.register('carousel', carouselController);
Stimulus.register('collection', collectionController);
Stimulus.register('menu', menuController);
Stimulus.register('country-menu', countryMenuController);
Stimulus.register('cart', perlgemCartController);
Stimulus.register('vto', vtoController);
Stimulus.register('shades', shadesController);
Stimulus.register('shades-dropdown', shadesDropdownController);
Stimulus.register('follow-element', followElementController);
Stimulus.register('search', searchController);
Stimulus.register('select-list', selectListController);
Stimulus.register('nav', navController);
Stimulus.register('number-incrementer', numberIncrementer);
Stimulus.register('video', videoController);
Stimulus.register('video-responsive', videoResponsiveController);
Stimulus.register('video-playback', videoSectionController);
Stimulus.register('tabbed-tout', tabbedToutController);
Stimulus.register('faq', faqController);
Stimulus.register('imageComparisonSlider', imageComparisonSliderController);
Stimulus.register('signup', signupController);
Stimulus.register('accordions', accordionsController);
Stimulus.register('accordions-list', accordionsListController);
Stimulus.register('horizontal-scroll', horizontalScrollController);
Stimulus.register('offers', offers_controller);
Stimulus.register('passwordStrength', passwordStrengthController);
Stimulus.register('promo', promoController);
Stimulus.register('stacked-nav', stackedNavController);
Stimulus.register('salon-commission', salonCommissionController);
Stimulus.register('container', containerController);
Stimulus.register('image-error', imageErrorController);
Stimulus.register('image-lazy', ImageLazyLoad);
Stimulus.register('power-reviews', PowerReviewsController);
Stimulus.register('countdown', countdownController);
Stimulus.register('focus-trap', focusTrapController);
Stimulus.register('loyalty-nav', loyaltyNavController);
Stimulus.register('loyalty-signup', loyaltySignUpController);
Stimulus.register('password-rules', passwordRulesController);
Stimulus.register('sticky-footer-observable', StickyFooterObservableController);
Stimulus.register('minimal-header', minimalHeaderController);
Stimulus.register('modal', modalController);
Stimulus.register('gnav-scroll', gnavScrollController);
Stimulus.register('sticky-footer-feed', StickyFooterFeedController);
Stimulus.register('lazy-video', LazyVideoController);
Stimulus.register('swiper-carousel', swiperCarouselController);
Stimulus.register('secondary-gnav', secondaryGnavController);
Stimulus.register('select', selectController);
Stimulus.register('iframe', iframeController);
Stimulus.register('account', accountController);
Stimulus.register('account-otp', accountOtpController);
Stimulus.register('account-guest', accountGuestController);
Stimulus.register('account-form-guest-checkout', accountFormGuestCheckoutController);
Stimulus.register('account-form-sign-in', accountFormSignInController);
Stimulus.register('account-form-sign-up', accountFormSignUpController);
Stimulus.register('dropdown', dropdownController);
Stimulus.register('language-selector', languageSelectorController);
Stimulus.register('revision-tag', revisionTagController);
Stimulus.register('cart-icon', cartIconController);
Stimulus.register('cart-item', cartItemController);
Stimulus.register('cart-drawer', cartDrawerController);
Stimulus.register('page-takeover', pageTakeover);
Stimulus.register('mobile-header', MobileHeaderController);
Stimulus.register('header-scroll', HeaderScrollController);
Stimulus.register('gnav-account', GnavAccountController);
Stimulus.register('live-chat-platform', liveChatController);
Stimulus.register('debug', debugController);
Stimulus.register('engraving', engravingController);

trackTiming('stimulus_started');

const isStaticPath = ['/sign-in', '/sign-up', '/guest-checkout', '/in-store-sign-up'].some(
  (path) => path === location.pathname
);

// Don't connect live socket(bind windows click event) for IAM(non live view) page
// liveSocket.disconnect doesn't remove windows click event, so just Not connect if we don't want click event to be added
if (!isStaticPath) {
  // connect if there are any LiveViews on the page
  trackTiming('live_socket_connecting');
  liveSocket.connect();

  // Override `history.scrollRestoration = "manual", as set by `phoenix_live_view.js`
  if (history.scrollRestoration) {
    history.scrollRestoration = 'auto';
  }

  trackTiming('live_socket_connected');
}

// Attach to PHX live_reload in local dev to jump to components
window.addEventListener('phx:live_reload:attached', ({ detail: reloader }) => {
  // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
  //
  //   * click with "c" key pressed to open at caller location
  //   * click with "d" key pressed to open at function component definition location
  let keyDown;
  window.addEventListener('keydown', (e) => (keyDown = e.key));
  window.addEventListener('keyup', (e) => (keyDown = null));
  window.addEventListener(
    'click',
    (e) => {
      if (keyDown === 'c') {
        e.preventDefault();
        e.stopImmediatePropagation();
        reloader.openEditorAtCaller(e.target);
      } else if (keyDown === 'd') {
        e.preventDefault();
        e.stopImmediatePropagation();
        reloader.openEditorAtDef(e.target);
      }
    },
    true
  );
  window.liveReloader = reloader;
});

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
// window.liveSocket = liveSocket

// Initializes LivePerson chat
const livePerson = new LivePerson();
livePerson.init();
